import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Shared frame for the August entry pages (landing and sign-in), laid out like the November admissions
 * portal: a type-only lockup, a two-column hero over a moving backdrop, a light sign-in card, a quiet footer.
 * August keeps its own colour: red accents on dark maroon where November uses champagne.
 */

// ── Icons (Lucide shapes, inline so the pages need no icon package) ─────────
const Svg = ({ size = 18, children, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...rest}>{children}</svg>
);
export const Icon = {
  Lock: (p) => <Svg {...p}><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Svg>,
  User: (p) => <Svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></Svg>,
  Users: (p) => <Svg {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>,
  Shield: (p) => <Svg {...p}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></Svg>,
  Arrow: (p) => <Svg {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Svg>,
  Check: (p) => <Svg strokeWidth="3" {...p}><path d="M20 6 9 17l-5-5" /></Svg>,
  Key: (p) => <Svg {...p}><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" /><circle cx="16.5" cy="7.5" r=".5" fill="currentColor" /></Svg>,
  Mail: (p) => <Svg {...p}><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></Svg>,
  Book: (p) => <Svg {...p}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></Svg>,
  Calendar: (p) => <Svg {...p}><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="m9 16 2 2 4-4" /></Svg>,
  List: (p) => <Svg {...p}><path d="M10 12h11M10 18h11M10 6h11" /><path d="M4 10h2M4 6h1v4M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></Svg>,
  Clock: (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></Svg>,
  Profile: (p) => <Svg {...p}><path d="M16 2v2M8 2v2" /><path d="M7 22v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" /><circle cx="12" cy="11" r="3" /><rect x="3" y="4" width="18" height="18" rx="2" /></Svg>,
  Eye: (p) => <Svg {...p}><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></Svg>,
  EyeOff: (p) => <Svg {...p}><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></Svg>,
};

// ── Styles ─────────────────────────────────────────────────────────────────
export const ENTRY_CSS = `
  .ae { --ae-bg: #170309; --ae-red: #e5484d; --ae-rose: #ff8f8f; --ae-blush: #ffd9d9; --ae-brand: #500000; --ae-brand-hover: #6e1420;
        --ae-ink: #18181b; --ae-muted: #52525b; --ae-line: #e4e4e7;
        min-height: 100vh; min-height: 100svh; display: flex; flex-direction: column; background: var(--ae-bg); color: #fff;
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif; -webkit-font-smoothing: antialiased; }
  .ae *, .ae *::before, .ae *::after { box-sizing: border-box; }
  .ae-wrap { width: 100%; max-width: 72rem; margin: 0 auto; padding-left: 16px; padding-right: 16px; }
  @media (min-width: 640px) { .ae-wrap { padding-left: 24px; padding-right: 24px; } }
  @media (min-width: 1024px) { .ae-wrap { padding-left: 32px; padding-right: 32px; } }
  .ae-display { font-family: "Inter Tight", Inter, ui-sans-serif, system-ui, sans-serif; }
  .ae-accent { background: linear-gradient(90deg, var(--ae-blush), var(--ae-rose) 55%, var(--ae-red)); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; padding-bottom: 0.14em; margin-bottom: -0.14em; }
  .ae-eyebrow { font-size: 12.5px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ae-rose); }

  /* Header */
  .ae-header { position: relative; z-index: 30; border-bottom: 1px solid rgba(255,255,255,0.07); }
  .ae-header.overlay { position: absolute; left: 0; right: 0; top: 0; }
  .ae-header .ae-wrap { height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .ae-lockup { display: flex; align-items: center; gap: 12px; color: #fff; text-decoration: none; background: none; border: 0; padding: 0; cursor: pointer; font: inherit; line-height: 1; }
  .ae-lockup b { font-family: "Inter Tight", Inter, sans-serif; font-size: 16px; font-weight: 700; letter-spacing: -0.03em; white-space: nowrap; }
  .ae-lockup b span { font-weight: 400; color: rgba(255,255,255,0.6); }
  .ae-lockup i { width: 1px; height: 16px; background: rgba(255,255,255,0.15); display: none; }
  .ae-lockup em { font-style: normal; font-size: 13px; color: rgba(255,255,255,0.6); display: none; }
  @media (min-width: 640px) { .ae-lockup i, .ae-lockup em { display: block; } }
  .ae-navlinks { display: flex; align-items: center; gap: 2px; }
  .ae-navlink { border: 0; background: none; font: inherit; cursor: pointer; border-radius: 6px; padding: 8px 8px; font-size: 14px; font-weight: 500; color: rgba(255,255,255,0.7); white-space: nowrap; text-decoration: none; transition: background .15s, color .15s; }
  .ae-navlink:hover { background: rgba(255,255,255,0.06); color: #fff; }
  @media (min-width: 640px) { .ae-navlink { padding: 8px 12px; } .ae-navlinks { gap: 4px; } }
  .ae-hide-sm { display: none; }
  @media (min-width: 640px) { .ae-hide-sm { display: inline; } }

  /* Backdrop: dark maroon with slow, faded red light drifting through it. */
  .ae-drift { position: absolute; inset: 0; overflow: hidden; z-index: -1; pointer-events: none;
              background: radial-gradient(120% 90% at 70% 0%, #2c0811 0%, #1a040a 45%, #0e0205 100%); }
  .ae-glow { position: absolute; border-radius: 50%; filter: blur(70px); will-change: transform; mix-blend-mode: screen; }
  .ae-glow.a { width: 62vmax; height: 34vmax; left: -10vmax; top: -6vmax; background: radial-gradient(closest-side, rgba(200,24,40,0.55), rgba(200,24,40,0)); animation: aeDriftA 46s ease-in-out infinite; }
  .ae-glow.b { width: 52vmax; height: 30vmax; right: -14vmax; top: 18vmax; background: radial-gradient(closest-side, rgba(150,8,28,0.6), rgba(150,8,28,0)); animation: aeDriftB 58s ease-in-out infinite; }
  .ae-glow.c { width: 40vmax; height: 22vmax; left: 30vmax; bottom: -14vmax; background: radial-gradient(closest-side, rgba(255,70,80,0.32), rgba(255,70,80,0)); animation: aeDriftC 52s ease-in-out infinite; }
  .ae-band { position: absolute; left: -40%; top: -30%; width: 45%; height: 160%; transform: rotate(18deg); filter: blur(30px);
             background: linear-gradient(90deg, rgba(255,80,90,0), rgba(255,80,90,0.09) 50%, rgba(255,80,90,0)); animation: aeBand 22s ease-in-out infinite; }
  .ae-scrim { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(20,3,7,0.55) 0%, rgba(20,3,7,0.25) 45%, transparent 70%); }
  .ae-grain { position: absolute; inset: 0; opacity: 0.06; mix-blend-mode: overlay;
              background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
  .ae-vignette { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, transparent 45%, rgba(0,0,0,0.5) 100%); }
  @keyframes aeDriftA { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(22vw,10vh) scale(1.15); } 66% { transform: translate(8vw,28vh) scale(0.92); } }
  @keyframes aeDriftB { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(-26vw,-8vh) scale(1.12); } 75% { transform: translate(-10vw,14vh) scale(0.9); } }
  @keyframes aeDriftC { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-18vw,-16vh) scale(1.2); } }
  @keyframes aeBand { 0% { transform: translateX(0) rotate(18deg); opacity: 0; } 15% { opacity: 1; } 55% { transform: translateX(300%) rotate(18deg); opacity: 0.8; } 70%, 100% { transform: translateX(330%) rotate(18deg); opacity: 0; } }
  .ae-sweepline { position: absolute; left: 0; right: 0; bottom: 0; height: 1px; overflow: hidden; background: rgba(255,255,255,0.08); }
  .ae-sweepline span { display: block; height: 1px; width: 50%; background: linear-gradient(90deg, transparent, var(--ae-red), transparent); animation: aeSweep 7s ease-in-out infinite; }
  @keyframes aeSweep { 0% { transform: translateX(-100%); opacity: 0; } 20% { opacity: 1; } 60%, 100% { transform: translateX(200%); opacity: 0; } }

  /* Entrances */
  .ae-enter { animation: aeEnter .42s cubic-bezier(.22,1,.36,1) both; }
  .ae-word { display: inline-block; animation: aeWord .9s cubic-bezier(.22,1,.36,1) both; }
  @keyframes aeEnter { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes aeWord { from { opacity: 0; transform: translateY(0.35em); filter: blur(8px); } to { opacity: 1; transform: none; filter: blur(0); } }

  /* Footer */
  .ae-footer { border-top: 1px solid rgba(255,255,255,0.07); background: rgba(0,0,0,0.2); }
  .ae-footer .ae-wrap { display: flex; flex-direction: column; gap: 8px; padding-top: 24px; padding-bottom: 24px; font-size: 12.5px; color: rgba(255,255,255,0.55); }
  @media (min-width: 640px) { .ae-footer .ae-wrap { flex-direction: row; justify-content: space-between; align-items: center; } }

  /* Contact dialog */
  .ae-modal-bg { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(10,1,3,0.6); backdrop-filter: blur(6px); }
  .ae-modal { width: 440px; max-width: 100%; border-radius: 16px; background: #fff; color: var(--ae-ink); padding: 28px; box-shadow: 0 40px 90px -30px rgba(0,0,0,0.75); position: relative; }
  .ae-modal h2 { margin: 0; font-family: "Inter Tight", Inter, sans-serif; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; }
  .ae-modal > p { margin: 6px 0 20px; color: var(--ae-muted); font-size: 14px; }
  .ae-field { display: grid; gap: 6px; margin-bottom: 14px; }
  .ae-field label { font-size: 13.5px; font-weight: 500; color: var(--ae-ink); }
  .ae-field input, .ae-field textarea { width: 100%; border: 1px solid #d4d4d8; border-radius: 10px; padding: 11px 13px; font: inherit; font-size: 14px; color: var(--ae-ink); outline: none; background: #fff; }
  .ae-field input:focus, .ae-field textarea:focus { border-color: var(--ae-brand); box-shadow: 0 0 0 3px rgba(80,0,0,0.12); }
  .ae-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 6px; }
  .ae-btn { border: 0; border-radius: 10px; padding: 11px 18px; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
  .ae-btn.primary { background: var(--ae-brand); color: #fff; }
  .ae-btn.primary:hover { background: var(--ae-brand-hover); }
  .ae-btn.primary:disabled { opacity: .6; cursor: default; }
  .ae-btn.ghost { background: transparent; color: var(--ae-muted); }
  .ae-btn.ghost:hover { background: #f3f3f5; }

  @media (prefers-reduced-motion: reduce) {
    .ae-glow, .ae-band, .ae-sweepline span, .ae-enter, .ae-word, .ae-marquee-track { animation: none !important; }
    .ae-band { opacity: 0; }
  }
`;

/** Dark maroon field with faded red light drifting slowly through it, under fine grain. */
export function MaroonDrift({ scrim = true }) {
  return (
    <div className="ae-drift" aria-hidden="true">
      <div className="ae-glow a" />
      <div className="ae-glow b" />
      <div className="ae-glow c" />
      <div className="ae-band" />
      {scrim && <div className="ae-scrim" />}
      <div className="ae-grain" />
      <div className="ae-vignette" />
    </div>
  );
}

/** "TRIP Internships | Texas A&M University", set in type like the November lockup. `product` is the word after
 *  TRIP (none on the tamutrip.com front door) and `home` is where the lockup leads. */
export function EntryHeader({ overlay = false, product = "Internships", home = "/august", label = "TRIP Internships", children }) {
  const navigate = useNavigate();
  return (
    <header className={`ae-header${overlay ? " overlay" : ""}`}>
      <div className="ae-wrap">
        <button type="button" className="ae-lockup" onClick={() => navigate(home)} aria-label={`${label}, Texas A&M University — home`}>
          <b>TRIP{product && <> <span>{product}</span></>}</b>
          <i />
          <em>Texas A&amp;M University</em>
        </button>
        <nav className="ae-navlinks" aria-label="Site">{children}</nav>
      </div>
    </header>
  );
}

export function EntryFooter({ note }) {
  return (
    <footer className="ae-footer">
      <div className="ae-wrap">
        <span>Trading, Risk &amp; Investments Program · Mays Business School · Texas A&amp;M University</span>
        <span>{note || "Students, board members and TRIP staff only."}</span>
      </div>
    </footer>
  );
}

/** "Contact admin": a message to the TRIP administrators, stored in Firestore (contactMessages). */
export function ContactDialog({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await addDoc(collection(db, "contactMessages"), { ...form, sentAt: new Date().toISOString(), read: false });
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => { onClose(); setSent(false); }, 2500);
    } catch { alert("Failed to send. Please try again."); }
    setSending(false);
  }

  return (
    <div className="ae-modal-bg" onClick={onClose}>
      <div className="ae-modal" role="dialog" aria-modal="true" aria-labelledby="contact-title" onClick={e => e.stopPropagation()}>
        <h2 id="contact-title">Contact admin</h2>
        <p>Send a message to the TRIP administrators.</p>
        {sent ? (
          <div style={{ padding: "16px 0", color: "#15803d", fontWeight: 600 }}>✓ Message sent. An administrator will reply by email.</div>
        ) : (
          <form onSubmit={submit}>
            {[{ label: "Name", key: "name", type: "text", placeholder: "Your full name" }, { label: "Email", key: "email", type: "email", placeholder: "you@tamu.edu" }].map(({ label, key, type, placeholder }) => (
              <div key={key} className="ae-field">
                <label htmlFor={`contact-${key}`}>{label}</label>
                <input id={`contact-${key}`} type={type} required placeholder={placeholder} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
              </div>
            ))}
            <div className="ae-field">
              <label htmlFor="contact-message">Message</label>
              <textarea id="contact-message" required rows={4} placeholder="How can we help?" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} style={{ resize: "vertical" }} />
            </div>
            <div className="ae-modal-actions">
              <button type="button" className="ae-btn ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="ae-btn primary" disabled={sending}>{sending ? "Sending…" : "Send message"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
